import { GridColumn as Column } from '@progress/kendo-react-grid';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  DeleteButton,
  SecondaryButton
} from 'src/components/shared/Button/Button';
import { DataGrid } from 'src/components/shared/DataGrid/DataGrid';
import { GridCountCell } from 'src/components/shared/DataGrid/GridCountCell';
import {
  HeaderSection,
  HeaderSectionLeft,
  HeaderSectionRight
} from 'src/components/shared/HeaderSection/HeaderSection';
import { Modal } from 'src/components/shared/Modal/Modal';
import { BodyType } from 'src/components/shared/Typography/BodyType';
import { Header1 } from 'src/components/shared/Typography/Header1';
import { useIsFormDirty } from 'src/hooks/useIsFormDirty';
import { useReduxDispatch } from 'src/store';
import { fetchInstrumentGroups } from 'src/store/instrumentgroups/instrumentgroups.actions';
import { fetchInstruments } from 'src/store/instruments/instruments.actions';
import { MethodDetailDto, MethodDto } from 'src/store/methods/method';
import { mapMethodDetailToEdit } from 'src/store/methods/method.map';
import {
  createMethod,
  deleteMethod,
  editMethod,
  fetchMethods
} from 'src/store/methods/methods.actions';
import { MethodApi } from 'src/store/methods/methods.api';
import { selectMethods } from 'src/store/methods/methods.selectors';
import { isLoadingMethods } from 'src/store/requests/requests.selectors';
import { MethodForm } from './MethodForm';

export const MethodsPage = () => {
  const [currentMethod, setCurrentMethod] = useState<MethodDto | null>(null);
  const [method, setMethod] = useState<Nullable<MethodDetailDto>>(null);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [isFormDirty, callbackRef] = useIsFormDirty();

  const { t } = useTranslation();
  const dispatch = useReduxDispatch();

  const methods = useSelector(selectMethods);
  const loadingMethods = useSelector(isLoadingMethods);

  useEffect(() => {
    dispatch(fetchInstrumentGroups());
    dispatch(fetchInstruments());
    dispatch(fetchMethods());
  }, [dispatch]);

  useEffect(() => {
    if (currentMethod) {
      setShowDeleteModal(true);
    }
  }, [currentMethod]);

  const onCancel = () => {
    setCurrentMethod(null);
    setMethod(null);
    setShowDeleteModal(false);
    setShowEditModal(false);
  };

  const onCreate = () => {
    setShowEditModal(true);
  };

  const onEdit = async (method: MethodDto) => {
    const response = await MethodApi.getById(method.id);
    if (response.ok) {
      setMethod(response.data);
      setShowEditModal(true);
    }
  };

  const onDelete = (method: MethodDto) => {
    setCurrentMethod(method);
  };

  const onConfirmDelete = () => {
    if (currentMethod) {
      dispatch(deleteMethod(currentMethod.id, currentMethod));
      setCurrentMethod(null);
      setShowDeleteModal(false);
    }
  };

  const onSubmit = async (method: MethodDetailDto) => {
    const dto = mapMethodDetailToEdit(method);
    if (dto.id) {
      await dispatch(editMethod(dto.id, dto));
    } else {
      await dispatch(createMethod(dto));
    }
    dispatch(fetchMethods());
    onCancel();
  };

  return (
    <>
      <HeaderSection className="mb-32">
        <HeaderSectionLeft>
          <Header1>{t('method.plural')}</Header1>
          <BodyType>{`${t('crudtext')} ${t('method.plural')}`}</BodyType>
        </HeaderSectionLeft>
        <HeaderSectionRight>
          <SecondaryButton onClick={onCreate} icon="la-plus">
            {`${t('create.verb')} ${t('method.singular')}`}
          </SecondaryButton>
        </HeaderSectionRight>
      </HeaderSection>

      <DataGrid
        items={methods}
        loading={loadingMethods}
        onRowClick={({ dataItem }) => onEdit(dataItem)}
        initialSort={[{ field: 'name', dir: 'asc' }]}
      >
        <Column
          field="name"
          title={t('props.name')}
          width="300px"
          footerCell={() => (
            <GridCountCell count={methods?.length} label={t('method.plural')} />
          )}
        />
        <Column field="description" title={t('props.description')} />
        <Column field="unit" title={t('props.unit')} width="200px" />
        <Column
          field="instrumentGroupName"
          title={t('instrumentGroup.singular')}
        />
        <Column field="instrumentName" title={t('method.instrument')} />
        <Column
          width="100px"
          filterable={false}
          cell={props => (
            <td>
              <DeleteButton onClick={() => onDelete(props.dataItem)} />
            </td>
          )}
        />
      </DataGrid>

      <Modal
        header={`${method ? t('edit') : t('create.verb')} ${t(
          'method.singular'
        )}`}
        confirmText={t('save.verb')}
        onClose={() => onCancel()}
        open={showEditModal}
        allowCommonExitStrategies={!isFormDirty}
      >
        <MethodForm
          method={method}
          onCancel={onCancel}
          onSubmit={onSubmit}
          ref={callbackRef}
        />
      </Modal>

      <Modal
        width={500}
        header={`${t('delete.verb')} ${t('method.singular')}`}
        confirmText={t('delete.verb')}
        onClose={() => onCancel()}
        onConfirm={() => onConfirmDelete()}
        open={showDeleteModal}
        showActions
      >
        <BodyType>
          {t('method.delete')} <q>{currentMethod?.name}</q>?
        </BodyType>
      </Modal>
    </>
  );
};
